import {SuspenseLoader} from '@orbit-ui/components/loader';
import {Separator} from '@orbit-ui/components/separator';
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@orbit-ui/components/sidebar';
import {cn} from '@orbit-ui/core';
import * as React from 'react';
import LogoSVG from '@/assets/images/cosmonic-logo.svg?react';
import {Breadcrumbs} from '@/components/breadcrumbs';
import {AppSidebar} from '@/features/core/components/app-sidebar';
import {useSidebarState} from '@/features/settings/hooks/useSidebarState';
import {type PropsWithSlots} from '@/types/props-with-slots';

type ShellPropsSlots = {
  aside: React.ElementType;
  main: React.ElementType;
  footer: React.ElementType;
};

type ShellProps = PropsWithSlots<ShellPropsSlots>;

function Shell({slots}: ShellProps) {
  const Aside = slots?.aside ?? React.Fragment;
  const Main = slots?.main ?? React.Fragment;
  const Footer = slots?.footer ?? React.Fragment;

  const {sidebarIsOpen, toggleSidebar} = useSidebarState();

  return (
    <SidebarProvider open={sidebarIsOpen} onOpenChange={toggleSidebar}>
      <AppSidebar
        slots={{
          header: ShellBranding,
          content: Aside,
          footer: Footer,
        }}
      />
      <SidebarInset className="overflow-hidden">
        <ShellHeader />
        <ShellMain>
          <Main />
        </ShellMain>
      </SidebarInset>
    </SidebarProvider>
  );
}

const ShellBranding = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(
  ({className, ...props}, ref) => (
    <div className="flex items-center gap-2" ref={ref} {...props}>
      <LogoSVG className="size-4 text-cosmo-purple" />
      <span className="text-sm font-medium leading-none text-foreground">Cosmonic Control</span>
    </div>
  ),
);
ShellBranding.displayName = 'ShellBranding';

const ShellHeader = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(
  ({className, ...props}, ref) => {
    const {open} = useSidebar();
    return (
      <header className={cn('flex h-10 shrink-0 items-center', className)} ref={ref} {...props}>
        <div className="flex items-center px-4">
          <div className="overflow-hidden">
            <div
              className={cn(
                'relative flex items-center gap-2 transition-all',
                open ? '-ml-10 opacity-0' : 'ml-0 opacity-100',
              )}
            >
              <SidebarTrigger />
              <Separator orientation="vertical" className="mr-2 h-4" />
            </div>
          </div>
          <Breadcrumbs />
        </div>
      </header>
    );
  },
);
ShellHeader.displayName = 'ShellHeader';

const ShellMain = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(
  ({children, className, ...props}, ref) => (
    <main className={cn('min-h-0 overflow-auto', className)} ref={ref} {...props}>
      <SuspenseLoader>{children}</SuspenseLoader>
    </main>
  ),
);
ShellMain.displayName = 'ShellMain';

export {Shell};
